/**
 * Installazione dell'app sul telefono (Andrea 2026-10-08).
 *
 * FantaSquad e' una PWA: aggiunta alla schermata Home si apre a schermo
 * intero con la sua icona, come un'app vera. Qui si capisce:
 * - se siamo GIA' dentro l'app installata (standalone);
 * - su che telefono siamo (iOS / Android), per mostrare la guida giusta;
 * - su Android/Chrome, si cattura l'evento `beforeinstallprompt` cosi' da
 *   poter aprire la finestra di installazione con un bottone nostro.
 *
 * Va importato presto (main.tsx): l'evento arriva una volta sola, poco dopo
 * il caricamento, e se nessuno lo ascolta si perde.
 */

import { useSyncExternalStore } from "react";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export type Piattaforma = "ios" | "android" | "altro";

let eventoInstallazione: BeforeInstallPromptEvent | null = null;
let installata = false;
const ascoltatori = new Set<() => void>();

function avvisa() {
  ascoltatori.forEach((f) => f());
}

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    eventoInstallazione = e as BeforeInstallPromptEvent;
    avvisa();
  });
  window.addEventListener("appinstalled", () => {
    eventoInstallazione = null;
    installata = true;
    avvisa();
  });
}

/** Siamo dentro l'app installata (icona in Home), non nel browser. */
export function inStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.matchMedia?.("(display-mode: fullscreen)").matches ||
    nav.standalone === true ||
    // dentro l'app Capacitor
    window.location.protocol === "capacitor:"
  );
}

export function piattaforma(): Piattaforma {
  if (typeof navigator === "undefined") return "altro";
  const ua = navigator.userAgent;
  // iPadOS si presenta come Mac ma ha il touch
  const ipad = /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
  if (/iPhone|iPad|iPod/.test(ua) || ipad) return "ios";
  if (/Android/.test(ua)) return "android";
  return "altro";
}

/** Su iOS l'installazione funziona solo da Safari (Chrome/Firefox iOS no). */
export function safariSuIos(): boolean {
  if (piattaforma() !== "ios") return false;
  const ua = navigator.userAgent;
  return /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
}

/** Link all'APK Android, se Andrea lo pubblica (VITE_APK_URL nel build). */
export const URL_APK: string | undefined = import.meta.env.VITE_APK_URL || undefined;

function iscrivi(f: () => void) {
  ascoltatori.add(f);
  return () => {
    ascoltatori.delete(f);
  };
}

function stato() {
  // valore stabile per useSyncExternalStore: cambia solo quando cambia davvero
  return `${eventoInstallazione ? 1 : 0}${installata ? 1 : 0}`;
}

/** Stato dell'installazione + azione per aprire la finestra del browser (Android). */
export function useInstallazione() {
  const s = useSyncExternalStore(iscrivi, stato, () => "00");
  const installabile = s[0] === "1";
  const appenaInstallata = s[1] === "1";
  const installa = async (): Promise<boolean> => {
    const ev = eventoInstallazione;
    if (!ev) return false;
    await ev.prompt();
    const scelta = await ev.userChoice;
    if (scelta.outcome === "accepted") {
      eventoInstallazione = null;
      avvisa();
      return true;
    }
    return false;
  };
  return { installabile, appenaInstallata, installa };
}
